import { ReactNode } from "react";

import { AutocompleteItem } from "@/components/ui/Forms/AutocompleteList";
import { SupportedLocale } from "@/enums/locale.enum";
import { Region } from "@/services/search.service";

interface BuildArgs {
  regionsListData?: Region[];
  locale: string;
  t: (key: string, lan?: SupportedLocale) => string;
  icons: {
    region: ReactNode;
    city: ReactNode;
    district: ReactNode;
    microdistrict: ReactNode;
    street: ReactNode;
    metroStation: ReactNode;
  };
}

export const EMPTY_SLOT = "null";

export const autocompleteBuildLocationOptions = ({
  regionsListData,
  locale,
  t,
  icons,
}: BuildArgs): { items: AutocompleteItem[]; regions: string[] } => {
  const items: AutocompleteItem[] = [];
  const regions: string[] = [];

  regionsListData?.forEach((region, regionIndex) => {
    const regionEnglishName = region.name.en;
    const availableLocales = Object.keys(region.name) as SupportedLocale[];

    regions.push(region.name[locale]);

    // Add region
    items.push(
      ...availableLocales.map((lan) => ({
        id: `region-${region._id ?? regionIndex + "-" + lan}`,
        icon: icons.region,
        title: region.name[lan],
        description: t("Region", lan),
        value: regionEnglishName,
        valueLocalized: region.name[lan],
        valueCurrentLocale: region.name[locale],
        regionName: regionEnglishName,
      })),
    );

    // Add cities
    region.cities?.forEach((city, cityIndex) => {
      const cityEnglishName = city.name.en;

      items.push(
        ...availableLocales.map((lan) => ({
          id: `city-${city._id ?? regionIndex + "-" + cityIndex + "-" + lan}`,
          icon: icons.city,
          title: city.name[lan],
          description: `${t("City", lan)} (${region.name[lan]})`,
          value: `${regionEnglishName}, ${cityEnglishName}`,
          valueLocalized: `${region.name[lan]}, ${city.name[lan]}`,
          valueCurrentLocale: `${region.name[locale]}, ${city.name[locale]}`,
          regionName: regionEnglishName,
          cityName: cityEnglishName,
        })),
      );

      // Add districts
      city.districts?.forEach((district, districtIndex) => {
        const districtEnglishName = district.name.en;

        items.push(
          ...availableLocales.map((lan) => ({
            id: `district-${district._id ?? regionIndex + "-" + cityIndex + "-" + districtIndex + "-" + lan}`,
            icon: icons.district,
            title: district.name[lan],
            description: `${t("District", lan)} (${city.name[lan]}, ${region.name[lan]})`,
            value: `${regionEnglishName}, ${cityEnglishName}, ${districtEnglishName}`,
            valueLocalized: `${region.name[lan]}, ${city.name[lan]}, ${district.name[lan]}`,
            valueCurrentLocale: `${region.name[locale]}, ${city.name[locale]}, ${district.name[locale]}`,
            regionName: regionEnglishName,
            cityName: cityEnglishName,
            districtName: districtEnglishName,
          })),
        );

        // Add microdistricts
        district.microdistricts.forEach((micro, microIndex) => {
          const microEnglishName = micro.name.en;

          items.push(
            ...availableLocales.map((lan) => ({
              id: `micro-${micro._id ?? regionIndex + "-" + cityIndex + "-" + districtIndex + "-" + microIndex + "-" + lan}`,
              icon: icons.microdistrict,
              title: `${micro.name[lan]}, ${city.name[lan]}`,
              description: t("Microdistrict", lan),
              value: `${region.name.en}, ${city.name.en}, ${district.name.en}, ${micro.name.en}`,
              valueLocalized: `${region.name[lan]}, ${city.name[lan]}, ${district.name[lan]}, ${micro.name[lan]}`,
              valueCurrentLocale: `${region.name[locale]}, ${city.name[locale]}, ${district.name[locale]}, ${micro.name[locale]}`,
              regionName: regionEnglishName,
              cityName: cityEnglishName,
              districtName: districtEnglishName,
              microdistrictName: microEnglishName,
            })),
          );
        });

        // Add streets
        district.streets.forEach((street, streetIndex) => {
          const streetEnglishName = street.name.en;

          items.push(
            ...availableLocales.map((lan) => ({
              id: `street-${street._id ?? regionIndex + "-" + cityIndex + "-" + districtIndex + "-" + streetIndex + "-" + lan}`,
              icon: icons.street,
              title: street.name[lan],
              description: `${t("Street", lan)} (${city.name[lan]})`,
              value: `${region.name.en}, ${city.name.en}, ${district.name.en}, ${street.name.en}`,
              valueLocalized: `${region.name[lan]}, ${city.name[lan]}, ${district.name[lan]}, ${street.name[lan]}`,
              valueCurrentLocale: `${region.name[locale]}, ${city.name[locale]}, ${district.name[locale]}, ${street.name[locale]}`,
              regionName: regionEnglishName,
              cityName: cityEnglishName,
              districtName: districtEnglishName,
              streetName: streetEnglishName,
            })),
          );
        });
      });

      // Add metro stations
      city.metroStations?.forEach((metroStation, metroStationIndex) => {
        const metroStationEnglishName = metroStation.name.en;

        items.push(
          ...availableLocales.map((lan) => ({
            id: `metroStation-${metroStation._id ?? regionIndex + "-" + cityIndex + "-0-0-" + metroStationIndex + "-" + lan}`,
            icon: icons.metroStation,
            title: metroStation.name[lan],
            description: `${t("Metro Station", lan)} (${city.name[lan]}, ${region.name[lan]})`,
            value: `${regionEnglishName}, ${cityEnglishName}, ${EMPTY_SLOT}, ${EMPTY_SLOT}, ${EMPTY_SLOT}, ${metroStationEnglishName}`,
            valueLocalized: `${region.name[lan]}, ${city.name[lan]}, ${metroStation.name[lan]}`,
            valueCurrentLocale: `${region.name[locale]}, ${city.name[locale]}, ${metroStation.name[locale]}`,
            regionName: regionEnglishName,
            cityName: cityEnglishName,
            metroStationName: metroStationEnglishName,
          })),
        );
      });
    });
  });

  return { items, regions };
};
